// src\contexts\authContext.tsx
"use client"
import { createContext, useContext, useEffect, useReducer, type ReactNode } from "react"
import { setCookie, deleteCookie, getCookie } from "cookies-next"
import { useRouter } from "next/navigation"
import { useCartStore } from "@/core/hooks/cart/useCartStore"
import LZString from "lz-string"

// Types
export interface User {
  id: number
  full_name: string
  mobile: string
  avatar: string
}

export interface AuthState {
  user: User | null
  token: string | null
  role: string | null
  permissions: string[]
  isAuthenticated: boolean
  isLoading: boolean
}

export interface LoginData {
  user: User
  token: string
  role: string
  permissions: string[]
  expires_at: string
  remember_me: boolean
}

type AuthAction =
  | { type: "SET_AUTH"; payload: LoginData }
  | { type: "CLEAR_AUTH" }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "UPDATE_USER"; payload: Partial<User> }

// Initial state
const initialState: AuthState = {
  user: null,
  token: null,
  role: null,
  permissions: [],
  isAuthenticated: false,
  isLoading: false,
}

// Reducer
function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case "SET_AUTH":
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        role: action.payload.role,
        permissions: action.payload.permissions,
        isAuthenticated: true,
        isLoading: false,
      }
    case "CLEAR_AUTH":
      return {
        ...initialState,
        isLoading: false,
      }
    case "SET_LOADING":
      return {
        ...state,
        isLoading: action.payload,
      }
    case "UPDATE_USER":
      return {
        ...state,
        user: state.user ? { ...state.user, ...action.payload } : null,
      }
    default:
      return state
  }
}

// Context
interface AuthContextType {
  state: AuthState
  setAuth: (data: LoginData) => void
  clearAuth: () => void
  updateUser: (userData: Partial<User>) => void
  hasPermission: (permissions: string | string[], mode?: "all" | "any") => boolean
  hasRole: (role: string) => boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Provider
interface AuthProviderProps {
  children: ReactNode
  initialState?: AuthState
}

export function AuthProvider({ children, initialState: serverInitialState }: AuthProviderProps) {
  const router = useRouter()
  const [state, dispatch] = useReducer(authReducer, serverInitialState || initialState)
  const { clearCart } = useCartStore.getState()


  const setAuth = (data: LoginData) => {
    const now = new Date()
    const expiresAt = new Date(data.expires_at)
    const maxAgeInSeconds = Math.floor((expiresAt.getTime() - now.getTime()) / 1000)

    try {
      const userStr = JSON.stringify(data.user)
      const permissionsStr = JSON.stringify(data.permissions)

      console.log("[v0] Original user size:", new Blob([userStr]).size, "bytes")
      console.log("[v0] Original permissions size:", new Blob([permissionsStr]).size, "bytes")

      // Use compressToBase64 instead of compressToUTF16
      const compressedUser = LZString.compressToBase64(userStr)
      const compressedPermissions = LZString.compressToBase64(permissionsStr)

      console.log("[v0] Compressed user size:", new Blob([compressedUser]).size, "bytes")
      console.log("[v0] Compressed permissions size:", new Blob([compressedPermissions]).size, "bytes")

      //const isHttps = process.env.NODE_ENV === 'production';
      const isHttp = process.env.NEXT_PUBLIC_FORCE_HTTP === "ON" || process.env.NODE_ENV !== 'production';

      const cookieOptions = {
        maxAge: maxAgeInSeconds,
        path: "/",
        sameSite: "lax" as const,
        secure: !isHttp,
      }

      setCookie("token", data.token, cookieOptions)
      setCookie("user", compressedUser, cookieOptions)
      setCookie("role", data.role, cookieOptions)
      setCookie("permissions", compressedPermissions, cookieOptions)

      console.log("[v0] Cookies set successfully")

      dispatch({ type: "SET_AUTH", payload: data })
    } catch (error) {
      console.error("[v0] Failed to set auth cookies:", error)
    }
  }

  const clearAuth = () => {
    router.push("/")
    deleteCookie("token", { path: "/" })
    deleteCookie("user", { path: "/" })
    deleteCookie("role", { path: "/" })
    deleteCookie("permissions", { path: "/" })
    dispatch({ type: "CLEAR_AUTH" })
    clearCart()
    deleteCookie("cart-uuid", { path: "/" })
  }


  const updateUser = (userData: Partial<User>) => {
    try {
      // Update the cookie
      const userCookie = getCookie("user")
      if (userCookie) {
        const decompressedUser = LZString.decompressFromBase64(userCookie as string)
        if (decompressedUser) {
          const currentUser = JSON.parse(decompressedUser)
          const updatedUser = { ...currentUser, ...userData }
          const compressedUser = LZString.compressToBase64(JSON.stringify(updatedUser))

          const isHttp = process.env.NEXT_PUBLIC_FORCE_HTTP === "ON" || process.env.NODE_ENV !== 'production'
          setCookie("user", compressedUser, {
            path: "/",
            sameSite: "lax" as const,
            secure: !isHttp,
          })
        }
      }
      // Update the state
      dispatch({ type: "UPDATE_USER", payload: userData })
    } catch (error) {
      console.error("Failed to update user:", error)
    }
  }

  const hasPermission = (permissions: string | string[], mode: "all" | "any" = "any"): boolean => {
    const permissionArray = Array.isArray(permissions) ? permissions : [permissions]
    if (mode === "all") {
      return permissionArray.every((perm) => state.permissions.includes(perm))
    }
    return permissionArray.some((perm) => state.permissions.includes(perm))
  }

  const hasRole = (role: string): boolean => {
    return state.role === role
  }

  useEffect(() => {
    const token = getCookie("token")
    const userCookie = getCookie("user")
    const role = getCookie("role")
    const permissionsCookie = getCookie("permissions")

    console.log("[v0] Hydrating from cookies:", {
      hasToken: !!token,
      hasUser: !!userCookie,
      hasRole: !!role,
      hasPermissions: !!permissionsCookie,
    })

    if (token && userCookie && role && permissionsCookie) {
      try {
        const decompressedUser = LZString.decompressFromBase64(userCookie as string)
        const decompressedPermissions = LZString.decompressFromBase64(permissionsCookie as string)

        if (!decompressedUser || !decompressedPermissions) {
          console.error("[v0] Failed to decompress cookies")
          clearAuth()
          return
        }

        const user = JSON.parse(decompressedUser)
        const permissions = JSON.parse(decompressedPermissions)

        console.log("[v0] Successfully hydrated auth state")

        dispatch({
          type: "SET_AUTH",
          payload: {
            user,
            token: token as string,
            role: role as string,
            permissions,
            expires_at: new Date().toISOString(),
            remember_me: true,
          },
        })
      } catch (error) {
        console.error("[v0] Failed to parse compressed cookies:", error)
        clearAuth()
      }
    }
  }, [])

  const value: AuthContextType = {
    state,
    setAuth,
    clearAuth,
    updateUser,
    hasPermission,
    hasRole,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// Custom hook
export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
